/** Section 2 — Follow-ups when an event is created (auto follow-ups, locked hearing prep, legacy rules). The old "when a file enters a bankruptcy-route
 *  status" rows are retired (14 Oct 2026): the Bankruptcy timeline's "After this step" settings are the only source of route actions. */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Plus, Trash2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { NumInput, EnumSelect, OWNER_ROLE_LABELS, KIND_LABELS, SectionIntro, TRIGGER_GROUP, ON_EXPIRE_LABELS } from "./processShared";

const DAY_BASIS = { calendar: "Calendar days", working: "Working days" };

function OnExpiryCell({ row, routeOptions, onChange, testId }) {
  const oe = typeof row.on_expire === "object" && row.on_expire ? row.on_expire : { kind: "none" };
  const set = (patch) => onChange({ ...oe, ...patch });
  return (
    <div className="space-y-1" data-testid={testId}>
      <EnumSelect value={oe.kind || "none"} options={ON_EXPIRE_LABELS} className="h-7 w-40" testId={`${testId}-kind`}
        onChange={(k) => onChange(k === "review_task" ? { kind: k, label: oe.label || `Review — ${row.label || "timer"} expired`, offset_days: 0, day_basis: "calendar" } : k === "route_status" ? { kind: k, route_status: oe.route_status || "post_demand_review", from_status: oe.from_status || null } : { kind: "none" })} />
      {oe.kind === "review_task" && (
        <Input value={oe.label || ""} onChange={(e) => set({ label: e.target.value })} className="h-7 text-xs w-40" placeholder="Review task label" data-testid={`${testId}-label`} />
      )}
      {oe.kind === "route_status" && (
        <div className="flex flex-col gap-1">
          <EnumSelect value={oe.route_status} options={routeOptions} className="h-7 w-40" testId={`${testId}-route`} onChange={(v) => set({ route_status: v })} />
          <EnumSelect value={oe.from_status || "_any"} options={{ _any: "from any route status", ...routeOptions }} className="h-7 w-40" testId={`${testId}-from`} onChange={(v) => set({ from_status: v === "_any" ? null : v })} />
        </div>
      )}
    </div>
  );
}

export default function ProcessTransitionsSection({ body, setBody, vocab, eventTypes }) {
  const rows = useMemo(() => body.transitions || [], [body.transitions]);
  const statutory = useMemo(() => new Set((vocab?.statutory_rows || []).map((r) => `${r.trigger}|${r.action_type}`)), [vocab]);
  const typeLabels = useMemo(() => Object.fromEntries((eventTypes || []).map((t) => [t.key, t.label])), [eventTypes]);
  const actionOptions = useMemo(() => ({ ...Object.fromEntries((eventTypes || []).filter((t) => t.key !== "other").map((t) => [t.key, t.label])), other: "Custom task (label below)" }), [eventTypes]);
  const [showLegacy, setShowLegacy] = useState(false);
  const routeStatuses = useMemo(() => (body.stages || []).filter((s) => !s.ladder), [body.stages]);
  const routeOptions = useMemo(() => Object.fromEntries(routeStatuses.map((s) => [s.key, s.label])), [routeStatuses]);

  const update = (id, patch) => setBody((b) => ({ ...b, transitions: b.transitions.map((r) => (r.id === id ? { ...r, ...patch } : r)) }));
  const remove = (id) => setBody((b) => ({ ...b, transitions: b.transitions.filter((r) => r.id !== id) }));
  const addRow = (trigger, triggerLabel, source) => setBody((b) => {
    const id = `${source === "hearing_prep" ? "hp" : "cu"}_${Date.now().toString(36)}`;
    const isPrep = source === "hearing_prep";
    return { ...b, transitions: [...b.transitions, {
      id, trigger, trigger_label: triggerLabel, action_type: "other", label: "", kind: "task",
      offset_days: isPrep ? -7 : 7, offset_from: isPrep ? "anchor" : "trigger", day_basis: "calendar",
      mandatory: isPrep, locked: isPrep, owner_role: "case_legal_handler", sla_days: null,
      on_expire: { kind: "none" }, statutory: false, source, enabled: true, parent_id: null,
    }] };
  });

  const retiredRoute = useMemo(() => rows.filter((r) => r.trigger.startsWith("route_entered:")), [rows]);
  const retiredPrep = useMemo(() => rows.filter((r) => r.source === "hearing_prep" && r.retired), [rows]);
  const groups = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      if (!showLegacy && r.source === "legacy_deadline_rules") continue;
      if (r.trigger.startsWith("route_entered:")) continue;
      if (r.source === "hearing_prep" && r.retired) continue;
      const key = r.trigger;
      if (!m.has(key)) m.set(key, { trigger: key, label: r.trigger_label, rows: [] });
      m.get(key).rows.push(r);
    }
    return [...m.values()].sort((a, b) => TRIGGER_GROUP(a.trigger).localeCompare(TRIGGER_GROUP(b.trigger)) || a.label.localeCompare(b.label));
  }, [rows, showLegacy]);

  const triggersWithoutRows = (eventTypes || []).filter((t) => t.key !== "other" && !rows.some((r) => r.trigger === `event_created:${t.key}`));
  const legacyCount = rows.filter((r) => r.source === "legacy_deadline_rules").length;

  return (
    <div className="space-y-4" data-testid="process-transitions-section">
      <SectionIntro title="Follow-ups when an event is created">
        One row = one thing the diary creates automatically when a Legal calendar event of that type is added. Offsets are days from the trigger event (negative = before an anchor such as the
        hearing); <strong>working days</strong> skip weekends and GOV.UK bank holidays. Statutory periods (SD2 18 / 21 days, Form 5 14 / 21 days) are locked.
        Mandatory rows can only be completed, never cancelled. <strong>On expiry</strong> says what happens when a timer passes unactioned:
        stay overdue, create a review task, or move the file along the bankruptcy route.
      </SectionIntro>
      <div className="rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-2 text-xs text-indigo-900" data-testid="route-actions-moved">
        <strong>Bankruptcy route actions moved.</strong> What the file gets when it enters a route status now comes from the Bankruptcy timeline — each step's
        <em> After this step</em> period + action under <Link to="/legal/timelines?mode=edit&timeline=bankruptcy" className="underline font-medium" data-testid="route-actions-moved-link">Legal settings → Timelines</Link>.
        {retiredRoute.length > 0 && <span> The {retiredRoute.length} old "when a file enters…" row{retiredRoute.length === 1 ? "" : "s"} are kept for history and never fire.</span>}
      </div>
      <div className="rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-2 text-xs text-indigo-900" data-testid="hearing-prep-moved">
        <strong>Hearing preparation moved to the timeline.</strong> The bundle / advocate rows that used to hang off a Court Hearing are now the
        <em> Hearing preparation</em> sub-timeline on the Bankruptcy Petition and Hearing steps — hearing date received → advocate booked → bundle → cover sheet → bankruptcy search, each action
        due N working days <em>before the Hearing date</em> (noted and created later when the date is not typed yet). Edit them under <Link to="/legal/timelines?mode=edit&timeline=hearing_prep" className="underline font-medium" data-testid="hearing-prep-moved-link">Legal settings → Timelines → Hearing preparation</Link>.
        {retiredPrep.length > 0 && <span> The {retiredPrep.length} old locked row{retiredPrep.length === 1 ? "" : "s"} {retiredPrep.length === 1 ? "is" : "are"} kept for history and never fire{retiredPrep.some((r) => r.moved_to) ? ` (${retiredPrep.filter((r) => r.moved_to).map((r) => `${r.label} → ${r.moved_to}`).join("; ")})` : ""}.</span>}
      </div>
      <div className="flex items-center gap-2 text-xs">
        <Switch checked={showLegacy} onCheckedChange={setShowLegacy} data-testid="transitions-show-legacy" />
        <span className="text-slate-600">Show legacy tracker rules ({legacyCount}) — retired with the Airtable tracker</span>
      </div>

      {groups.map((g) => (
        <div key={g.trigger} className="border rounded-md" data-testid={`trigger-group-${g.trigger.replace(/[:]/g, "-")}`}>
          <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b">
            <div>
              <span className="text-[10px] uppercase tracking-wide text-slate-500">{TRIGGER_GROUP(g.trigger)}</span>
              <div className="text-sm font-semibold text-slate-800">{g.label}</div>
            </div>
            {g.trigger.startsWith("event_created:") && (
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => addRow(g.trigger, g.label, "auto_followups")} data-testid={`add-followup-${g.trigger.split(":")[1]}`}>
                  <Plus className="w-3 h-3 mr-1" /> Add follow-up
                </Button>

              </div>
            )}
          </div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-3 py-1.5 font-medium">Creates</th>
                <th className="text-left px-2 py-1.5 font-medium w-24">Kind</th>
                <th className="text-left px-2 py-1.5 font-medium w-24">Offset</th>
                <th className="text-left px-2 py-1.5 font-medium w-32">Basis</th>
                <th className="text-left px-2 py-1.5 font-medium w-44">Owner</th>
                <th className="text-left px-2 py-1.5 font-medium w-20">SLA d</th>
                <th className="text-center px-2 py-1.5 font-medium w-20">Mandatory</th>
                <th className="text-left px-2 py-1.5 font-medium w-44">On expiry</th>
                <th className="text-center px-2 py-1.5 font-medium w-14">On</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {g.rows.map((r) => {
                const isStat = statutory.has(`${r.trigger}|${r.action_type}`);
                const isPrep = r.source === "hearing_prep";
                const customLabel = r.action_type === "other" || isPrep;
                return (
                  <tr key={r.id} className={!r.enabled ? "opacity-50" : ""} data-testid={`transition-row-${r.id}`}>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1.5">
                        {isStat && <ShieldCheck className="w-3.5 h-3.5 text-indigo-600 shrink-0" title="Statutory period — locked" />}
                        {isPrep && <Lock className="w-3.5 h-3.5 text-slate-500 shrink-0" title="Locked hearing prep — anchored to the hearing" />}
                        {!isPrep && !isStat && (
                          <EnumSelect value={r.action_type} options={actionOptions} className="h-7 w-44 shrink-0" testId={`transition-type-${r.id}`}
                            onChange={(v) => update(r.id, { action_type: v, label: v === "other" ? (r.action_type === "other" ? r.label : "") : (typeLabels[v] || v) })} />
                        )}
                        {customLabel
                          ? <Input value={r.label} placeholder="Task label" onChange={(e) => update(r.id, { label: e.target.value })} className="h-7 text-xs" data-testid={`transition-label-${r.id}`} />
                          : (isStat && <span className="font-medium">{typeLabels[r.action_type] || r.label}</span>)}
                      </div>
                      {isStat && <div className="text-[10px] text-indigo-700 mt-0.5">Statutory — {r.offset_days} calendar days, always on</div>}
                    </td>
                    <td className="px-2 py-1.5"><EnumSelect value={r.kind} onChange={(v) => update(r.id, { kind: v })} options={KIND_LABELS} className="h-7" disabled={isStat} testId={`transition-kind-${r.id}`} /></td>
                    <td className="px-2 py-1.5"><NumInput value={r.offset_days} min={-90} max={365} onChange={(v) => update(r.id, { offset_days: v })} className="h-7 w-20" disabled={isStat} testId={`transition-offset-${r.id}`} /></td>
                    <td className="px-2 py-1.5"><EnumSelect value={r.day_basis} onChange={(v) => update(r.id, { day_basis: v })} options={DAY_BASIS} className="h-7" disabled={isStat} testId={`transition-basis-${r.id}`} /></td>
                    <td className="px-2 py-1.5"><EnumSelect value={r.owner_role} onChange={(v) => update(r.id, { owner_role: v })} options={OWNER_ROLE_LABELS} className="h-7" testId={`transition-owner-${r.id}`} /></td>
                    <td className="px-2 py-1.5"><NumInput value={r.sla_days} min={0} max={365} onChange={(v) => update(r.id, { sla_days: v })} className="h-7 w-16" placeholder="—" testId={`transition-sla-${r.id}`} /></td>
                    <td className="px-2 py-1.5 text-center"><Checkbox checked={!!r.mandatory} onCheckedChange={(v) => update(r.id, { mandatory: !!v })} disabled={isPrep} data-testid={`transition-mandatory-${r.id}`} /></td>
                    <td className="px-2 py-1.5 align-top">
                      {r.kind === "timer" || (r.on_expire && r.on_expire.kind && r.on_expire.kind !== "none")
                        ? <OnExpiryCell row={r} routeOptions={routeOptions} onChange={(oe) => update(r.id, { on_expire: oe })} testId={`transition-expire-${r.id}`} />
                        : <span className="text-[10px] text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-center"><Switch checked={r.enabled !== false} onCheckedChange={(v) => update(r.id, { enabled: v })} disabled={isStat} className="scale-75" data-testid={`transition-enabled-${r.id}`} /></td>
                    <td className="px-1 py-1.5 text-right">
                      {!isStat && (r.source === "hearing_prep" || r.source === "route_entry" || r.action_type === "other" || r.id.startsWith("cu_")) && (
                        <Button size="sm" variant="ghost" className="h-7 px-1.5 text-rose-600" onClick={() => remove(r.id)} data-testid={`transition-remove-${r.id}`}><Trash2 className="w-3 h-3" /></Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {g.rows.length === 0 && <tr><td colSpan={10} className="px-3 py-2 text-[11px] italic text-muted-foreground">No rows — nothing is created automatically for this trigger.</td></tr>}
            </tbody>
          </table>
        </div>
      ))}

      {triggersWithoutRows.length > 0 && (
        <div className="border border-dashed rounded-md p-3">
          <div className="text-[11px] text-slate-600 mb-2">Event types with no automatic follow-up yet — add one to start a chain:</div>
          <div className="flex flex-wrap gap-1.5">
            {triggersWithoutRows.map((t) => (
              <Button key={t.key} size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => addRow(`event_created:${t.key}`, t.label, "auto_followups")} data-testid={`add-trigger-${t.key}`}>
                <Plus className="w-3 h-3 mr-1" /> {t.label}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
